import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { experience, education, languages } from '../data/profileData';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.07, delayChildren: 0.1 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

function EYUnifiedEntry({ eyEntries, isOpen, onToggle }) {
  const senior = eyEntries.find((e) => e.id === 'EY-SENIOR');
  const consultant = eyEntries.find((e) => e.id === 'EY-CONSULTANT');
  const [activeScope, setActiveScope] = useState('governance');

  const scopeDetails = {
    governance: {
      title: 'Governance & Maturity Frameworks',
      frameworks: ['ISO/IEC 27001', 'NIST CSF', 'COBIT 2019'],
      summary: 'Translating technical security postures into executive and board recommendations. Assessing cybersecurity maturity, identifying organizational gaps, and designing pragmatic governance roadmaps.',
    },
    testing: {
      title: 'Controls Testing & Gap Analysis',
      frameworks: ['Access Reviews', 'Segregation of Duties', 'Control Walkthroughs'],
      summary: 'Conducting rigorous walkthroughs of key security controls across identity governance, change management, and configuration baselines to test operating effectiveness.',
    },
    operations: {
      title: 'IT Operations & Service Management',
      frameworks: ['ISO/IEC 20000', 'SLA Adherence', 'Incident Escalation'],
      summary: 'Reviewing operational service delivery, incident response procedures, and service level agreement (SLA) adherence for mission-critical enterprise environments.',
    },
  };

  return (
    <div className="border-b border-stone-200 pb-7 md:pb-8 pt-2 md:pt-4">
      <div className="flex flex-col md:flex-row md:items-start md:gap-4">
        {/* Overall start and end period: desktop left column */}
        <div className="hidden md:block md:flex-shrink-0 md:w-28 pt-0.5">
          <span className="font-mono text-[10px] text-stone-500 uppercase tracking-wider block">
            Aug 2024
          </span>
          <span className="font-mono text-[9px] text-stone-500 uppercase tracking-wider block mt-0.5">
            Present
          </span>
        </div>

        {/* Company & Progression */}
        <div className="flex-grow min-w-0">
          {/* Company header */}
          <div className="mb-4 md:mb-5">
            <div className="md:hidden font-mono text-[9.5px] uppercase tracking-wider text-stone-500 mb-1">
              Aug 2024 - Present
            </div>
            <h3 className="text-base font-semibold text-stone-900 leading-snug">
              Ernst &amp; Young (EY Indonesia)
            </h3>
            <p className="text-[11px] text-stone-500 font-mono mt-0.5">
              Jakarta, Indonesia · Full-time
            </p>
          </div>

          {/* Stepper connecting the roles */}
          <div className="relative pl-5 md:pl-6 ml-1 md:ml-1.5 space-y-6 md:space-y-7">
            <motion.span
              initial={{ scaleY: 0 }}
              whileInView={{ scaleY: 1 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.65, ease: [0.16, 1, 0.3, 1] }}
              className="absolute left-0 top-0 bottom-0 w-[2px] bg-stone-300 origin-top"
              aria-hidden="true"
            />
            {/* 1. Senior Role */}
            {senior && (
              <motion.div initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: 0.15 }} className="relative">
                {/* Stepper node dot */}
                <div 
                  className="absolute -left-[27px] md:-left-[31px] top-1 w-3 h-3 rounded-full bg-stone-900 border-2 border-white ring-2 ring-yellow-500/80 shadow-sm" 
                  aria-hidden="true" 
                />
                
                <div className="flex flex-wrap sm:flex-nowrap items-baseline gap-2 sm:gap-3">
                  <span className="text-sm font-semibold text-stone-900 leading-snug">
                    {senior.role}
                  </span>
                  <span className="inline-flex items-center gap-1.5 font-mono text-[9px] text-yellow-800 bg-yellow-50 uppercase tracking-widest border border-yellow-300 px-2 py-0.5 rounded-sm leading-none w-fit font-medium">
                    <span className="w-1.5 h-1.5 rounded-full bg-yellow-500 animate-pulse" aria-hidden="true" />
                    Active
                  </span>
                </div>
                <p className="text-[11px] text-stone-500 font-mono mt-0.5">
                  {senior.period}
                </p>

                {/* Interactive Scope Inspector Tabs */}
                <div className="mt-3.5 border border-stone-200 bg-stone-50/70 p-3 sm:p-3.5 rounded-sm">
                  <div className="flex items-center justify-between border-b border-stone-200/80 pb-2 mb-2.5">
                    <span className="font-mono text-[8.5px] uppercase tracking-[0.2em] text-stone-500">
                      Engagement Scope Inspector
                    </span>
                    <span className="font-mono text-[8.5px] text-stone-400">
                      Select Focus
                    </span>
                  </div>

                  {/* Scope Selector Pills */}
                  <div className="grid grid-cols-3 gap-1.5 mb-2.5" role="tablist" aria-label="Engagement scopes">
                    {[
                      { id: 'governance', label: 'Governance & Maturity' },
                      { id: 'testing', label: 'Controls Testing' },
                      { id: 'operations', label: 'IT Ops & SLA' },
                    ].map((tab) => {
                      const isActive = activeScope === tab.id;
                      return (
                        <button
                          key={tab.id}
                          role="tab"
                          aria-selected={isActive}
                          onClick={() => setActiveScope(tab.id)}
                          className={`text-left px-2 py-1.5 border transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 rounded-sm ${
                            isActive
                              ? 'bg-stone-900 text-stone-50 border-stone-900 shadow-sm'
                              : 'bg-white text-stone-600 border-stone-200 hover:bg-stone-100 hover:text-stone-900'
                          }`}
                        >
                          <span className="text-[10px] sm:text-[10.5px] font-medium leading-tight block truncate">
                            {tab.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  {/* Active Scope Content */}
                  <div className="bg-white border border-stone-200/80 p-2.5 sm:p-3 rounded-sm">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs font-semibold text-stone-800">
                        {scopeDetails[activeScope].title}
                      </span>
                    </div>
                    <p className="text-[11.5px] sm:text-[12px] text-stone-600 leading-relaxed font-light mb-2">
                      {scopeDetails[activeScope].summary}
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {scopeDetails[activeScope].frameworks.map((tag) => (
                        <span
                          key={tag}
                          className="font-mono text-[8.5px] text-stone-600 bg-stone-50 border border-stone-200 px-1.5 py-0.5 rounded-sm"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* 2. Consultant Role (Previous) */}
            {consultant && (
              <motion.div initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: 0.3 }} className="relative">
                {/* Stepper node dot */}
                <div 
                  className="absolute -left-[25px] md:-left-[29px] top-1.5 w-2 h-2 rounded-full bg-stone-400 border border-white" 
                  aria-hidden="true" 
                />
                
                <div className="flex items-start justify-between gap-3 md:gap-4">
                  <div>
                    <span className="text-sm font-medium text-stone-800 leading-snug block">
                      {consultant.role}
                    </span>
                    <p className="text-[11px] text-stone-500 font-mono mt-0.5">
                      {consultant.period}
                    </p>
                  </div>

                  <button
                    onClick={onToggle}
                    aria-expanded={isOpen}
                    className="flex-shrink-0 min-w-11 min-h-11 flex items-center justify-center text-stone-600 hover:text-stone-900 transition-colors text-sm font-mono focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 rounded-sm"
                    aria-label="Toggle role highlights"
                  >
                    {isOpen ? '−' : '+'}
                  </button>
                </div>

                {/* Role highlights */}
                {isOpen && consultant.highlights && (
                  <div className="mt-3 pt-1">
                    <ul className="space-y-2">
                      {consultant.highlights.map((h, i) => (
                        <li key={i} className="flex gap-2 text-[12px] md:text-[12.5px] text-stone-600 leading-relaxed">
                          <span className="text-stone-300 flex-shrink-0 mt-0.5">·</span>
                          <span>{h}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ExperienceEntry({ entry, isOpen, onToggle }) {
  const hasDetail =
    (entry.highlights && entry.highlights.length > 0) ||
    entry.description;

  const [startPeriod, endPeriod] = entry.period.includes(' - ')
    ? entry.period.split(' - ')
    : [entry.period, ''];

  return (
    <div className="border-b border-stone-200 last:border-b-0">
      <button
        onClick={hasDetail ? onToggle : undefined}
        disabled={!hasDetail}
        aria-expanded={hasDetail ? isOpen : undefined}
        className={`w-full text-left py-4 md:py-5 flex flex-col md:flex-row md:items-start md:gap-4 group focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 ${hasDetail ? 'cursor-pointer' : 'cursor-default'}`}
      >
        {/* Desktop: timeline left column */}
        <div className="hidden md:block md:flex-shrink-0 md:w-28 pt-0.5">
          <span className="font-mono text-[10px] text-stone-500 uppercase tracking-wider block">
            {startPeriod}
          </span>
          {endPeriod && (
            <span className="font-mono text-[9px] text-stone-500 uppercase tracking-wider block mt-0.5">
              {endPeriod}
            </span>
          )}
        </div>

        {/* Mobile: compact date above */}
        <div className="md:hidden font-mono text-[9.5px] uppercase tracking-wider text-stone-500 mb-1">
          {entry.period}
        </div>

        <div className="flex-grow min-w-0 w-full flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap sm:flex-nowrap items-baseline gap-2 sm:gap-3">
              <span className="text-sm font-medium text-stone-900 leading-snug">
                {entry.role}
              </span>
              {entry.active && (
                <span className="inline-block font-mono text-[9px] text-yellow-600 uppercase tracking-widest border border-yellow-300 px-1.5 py-0.5 rounded-sm leading-none w-fit">
                  Active
                </span>
              )}
            </div>
            <p className="text-[12px] text-stone-600 mt-0.5 font-medium">{entry.org}</p>
            <p className="text-[11px] text-stone-500 mt-0.5 font-mono">
              {entry.type}{entry.location ? ` · ${entry.location}` : ''}
            </p>
          </div>

          {hasDetail && (
            <div className="flex-shrink-0 pt-0.5">
              <span
                className="block w-5 h-5 text-stone-500 group-hover:text-stone-700 transition-colors text-sm font-mono text-center"
                aria-hidden="true"
              >
                {isOpen ? '−' : '+'}
              </span>
            </div>
          )}
        </div>
      </button>

      {/* Expandable detail */}
      {hasDetail && isOpen && (
        <div className="pb-4 pl-3 md:pl-32 pr-2 md:pr-4 border-l-2 border-stone-200 md:border-l-0 ml-1 md:ml-0 my-1">
          {entry.highlights && (
            <ul className="space-y-2">
              {entry.highlights.map((h, i) => (
                <li key={i} className="flex gap-2 text-[12px] md:text-[13px] text-stone-600 leading-relaxed">
                  <span className="text-stone-300 flex-shrink-0 mt-0.5">·</span>
                  <span>{h}</span>
                </li>
              ))}
            </ul>
          )}
          {entry.skills && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {entry.skills.map((s) => (
                <span
                  key={s}
                  className="text-[9.5px] md:text-[10px] font-mono text-stone-500 border border-stone-200 px-2 py-0.5 uppercase tracking-tight"
                >
                  {s}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Experience() {
  const [openIds, setOpenIds] = useState(new Set(['EY-CONSULTANT']));
  const [showAllMobile, setShowAllMobile] = useState(false);

  const toggle = (id) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // Group entries
  const eyEntries = experience.filter((e) => e.id.startsWith('EY'));
  const otherEntries = experience.filter((e) => !e.id.startsWith('EY'));

  // On mobile: show first other entry (GenBI) by default, collapse remainder
  const primaryOther = otherEntries.slice(0, 1);
  const secondaryOther = otherEntries.slice(1);

  return (
    <section id="section-experience" className="snap-start min-h-screen bg-white w-full px-4 sm:px-5 py-10 sm:py-12 md:p-20">
      <div className="max-w-6xl mx-auto pt-4">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          {/* Section header */}
          <motion.div
            variants={itemVariants}
            className="flex flex-col sm:flex-row sm:items-baseline justify-between border-b border-stone-900 pb-4 mb-6 md:mb-8 gap-2"
          >
            <div>
              <h2 className="text-xl sm:text-2xl font-serif italic text-stone-900">
                Experience &amp; Track Record
              </h2>
            </div>
            <span className="font-mono text-[9px] uppercase tracking-widest text-stone-500">
              Ernst &amp; Young &amp; Prior Background
            </span>
          </motion.div>

          {/* Unified EY Company Block with career progression */}
          <motion.div variants={itemVariants} className="mb-2">
            <EYUnifiedEntry
              eyEntries={eyEntries}
              isOpen={openIds.has('EY-CONSULTANT')}
              onToggle={() => toggle('EY-CONSULTANT')}
            />
          </motion.div>

          {/* Other entries */}
          <motion.div variants={itemVariants}>
            {/* Primary non-EY entry */}
            {primaryOther.map((entry) => (
              <ExperienceEntry
                key={entry.id}
                entry={entry}
                isOpen={openIds.has(entry.id)}
                onToggle={() => toggle(entry.id)}
              />
            ))}

            {/* Desktop: always show all secondary entries */}
            <div className="hidden md:block">
              {secondaryOther.map((entry) => (
                <ExperienceEntry
                  key={entry.id}
                  entry={entry}
                  isOpen={openIds.has(entry.id)}
                  onToggle={() => toggle(entry.id)}
                />
              ))}
            </div>

            {/* Mobile: collapsible for remaining positions */}
            {secondaryOther.length > 0 && (
              <div className="md:hidden">
                {showAllMobile && (
                  <div>
                    {secondaryOther.map((entry) => (
                      <ExperienceEntry
                        key={entry.id}
                        entry={entry}
                        isOpen={openIds.has(entry.id)}
                        onToggle={() => toggle(entry.id)}
                      />
                    ))}
                  </div>
                )}

                <button
                  onClick={() => setShowAllMobile((v) => !v)}
                  className="w-full min-h-11 py-3 my-2 text-center text-[10px] font-mono uppercase tracking-[0.2em] text-stone-500 hover:text-stone-900 border border-stone-200 hover:border-stone-400 transition-colors rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-900"
                >
                  {showAllMobile
                    ? 'Show fewer positions ↑'
                    : `Prior background & university teams (${secondaryOther.length} more) ↓`}
                </button>
              </div>
            )}
          </motion.div>

          {/* Education & Academic Specimen */}
          <motion.div variants={itemVariants} className="mt-12 md:mt-16">
            <div className="flex items-baseline justify-between border-b border-stone-200 pb-4 mb-6">
              <h2 className="text-xl font-serif italic text-stone-900">Education</h2>
            </div>
            {education.map((edu) => {
              const [eduStart, eduEnd] = edu.period.includes(' - ')
                ? edu.period.split(' - ')
                : [edu.period, ''];
              const isUI = edu.id === 'UI';
              return (
                <div key={edu.id} className="border-b border-stone-100 py-4 md:py-5 flex flex-col md:flex-row md:gap-4 last:border-b-0">
                  {/* Desktop left column */}
                  <div className="hidden md:block md:flex-shrink-0 md:w-28 pt-0.5">
                    <span className="font-mono text-[10px] text-stone-500 uppercase tracking-wider block">
                      {eduStart}
                    </span>
                    {eduEnd && (
                      <span className="font-mono text-[9px] text-stone-500 uppercase tracking-wider block mt-0.5">
                        {eduEnd}
                      </span>
                    )}
                  </div>
                  {/* Mobile compact date */}
                  <div className="md:hidden font-mono text-[9.5px] uppercase tracking-wider text-stone-500 mb-1">
                    {edu.period}
                  </div>
                  <div className="flex-grow">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <p className="text-sm font-semibold text-stone-900">{edu.institution}</p>
                      {isUI && (
                        <span className="font-mono text-[8.5px] uppercase tracking-widest text-stone-500 bg-stone-100 border border-stone-200 px-1.5 py-0.5 rounded-sm">
                          Faculty of Engineering
                        </span>
                      )}
                    </div>
                    <p className="text-[12.5px] text-stone-600 mt-0.5">{edu.degree}</p>
                    {isUI && (
                      <div className="mt-2.5 flex flex-wrap gap-1.5">
                        {['Computer Engineering', 'Network Systems', 'Computer Vision & IoT', 'Systems Architecture'].map((spec) => (
                          <span key={spec} className="font-mono text-[8.5px] text-stone-500 bg-stone-50 border border-stone-200 px-1.5 py-0.5 rounded-sm">
                            {spec}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </motion.div>

          {/* Languages Specimen */}
          <motion.div variants={itemVariants} className="mt-12 md:mt-14">
            <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-stone-500 mb-3">
              Languages &amp; Proficiency
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 border-t border-stone-200 pt-4">
              {languages.map((l) => (
                <div key={l.lang} className="border border-stone-200 bg-stone-50/60 p-3 rounded-sm flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-stone-900 tracking-wider font-mono uppercase">{l.lang}</span>
                      <span className="font-mono text-[8.5px] text-stone-400">
                        {l.lang === 'Indonesian' ? 'Native' : l.lang === 'English' ? 'C1/C2' : 'A1/A2'}
                      </span>
                    </div>
                    <span className="text-[11px] text-stone-600 mt-1 block">{l.level}</span>
                  </div>
                  {/* Subtle qualitative tick bar */}
                  <div className="flex gap-1 mt-3">
                    {[1, 2, 3, 4, 5].map((tick) => {
                      const filled =
                        (l.lang === 'Indonesian' && tick <= 5) ||
                        (l.lang === 'English' && tick <= 4) ||
                        (l.lang === 'Japanese' && tick <= 2);
                      return (
                        <div
                          key={tick}
                          className={`h-1 flex-1 rounded-xs transition-colors ${
                            filled ? 'bg-stone-800' : 'bg-stone-200'
                          }`}
                        />
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
